import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useReactTable,
    createColumnHelper,
    getCoreRowModel,
    SortingState,
    getSortedRowModel,
    ColumnFiltersState,
    FilterFn,
    getFilteredRowModel,
    getFacetedUniqueValues,
    getFacetedMinMaxValues,
    getPaginationRowModel,
} from '@tanstack/react-table';
import { rankItem } from '@tanstack/match-sorter-utils';
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    MoreVertical,
    Inbox,
} from 'lucide-react';
import { format } from 'date-fns';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import { Badge } from '@/src/shadComponents/ui/badge';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/src/shadComponents/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/src/shadComponents/ui/select';

interface TableProps {
    columns: any[];
    data: any[];
    enableSorting?: boolean;
    enablePagination?: boolean;
    onDelete: () => void;
    onClone: (id: number, activation: any) => void;
}

declare module '@tanstack/table-core' {
    interface FilterFns {
        fuzzy: FilterFn<unknown>;
    }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
};

export default function ActivationsTable({
    columns,
    data,
    onDelete,
    enableSorting = false,
    enablePagination = false,
    onClone,
}: TableProps) {
    const navigate = useNavigate();
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const authenticatedApi = useAuthenticatedApi();
    const [columnFilters, setColumnFilters] =
        React.useState<ColumnFiltersState>([]);

    const options = {
        data,
        columns,
        filterFns: { fuzzy: fuzzyFilter },
        state: { columnFilters, sorting },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        getCoreRowModel: getCoreRowModel(),
    };

    if (enablePagination) {
        // @ts-ignore
        options.getPaginationRowModel = getPaginationRowModel();
    }

    // @ts-ignore
    const table = useReactTable(options);

    useEffect(() => {
        if (enablePagination && table) {
            // @ts-ignore
            table.setPageSize(25);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const performDelete = (id: number, name: string) => {
        const deleteRequest = async () => {
            const response = await authenticatedApi.put(
                `activations/${id}/archive`,
            );
            if (response.status === 200) {
                onDelete();
            }
        };

        const shouldDelete = window.confirm(
            `Are you sure you would like to archive ${name}`,
        );

        if (shouldDelete) {
            deleteRequest();
        }
    };

    return (
        <div className="space-y-4">
            <div className="rounded-md border">
                {table.getRowModel().rows.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground py-12">
                        <Inbox className="size-8" />
                        <span className="text-sm">No activations found</span>
                    </div>
                ) : (
                    <div className="divide-y divide-border">
                        {table.getRowModel().rows.map((row) => (
                            <div
                                key={row.getValue('id')}
                                className="flex items-center justify-between gap-x-4 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                                onClick={() =>
                                    navigate(
                                        `/activations/${row.getValue('id')}`,
                                    )
                                }
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <p className="text-sm font-medium text-foreground truncate">
                                            {row.getValue('name')}
                                        </p>
                                        {row.original.isArchived && (
                                            <Badge
                                                variant="secondary"
                                                className="shrink-0"
                                            >
                                                Archived
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                                        <time
                                            dateTime={row.getValue('date')}
                                        >
                                            {format(
                                                new Date(
                                                    `${
                                                        (
                                                            row.getValue(
                                                                'date',
                                                            ) as string
                                                        ).split('T')[0]
                                                    }T23:59:59Z`,
                                                ),
                                                'MMM d, yyyy',
                                            )}
                                        </time>
                                        <span className="text-border">|</span>
                                        <span>
                                            {row.getValue('location')}
                                        </span>
                                        <span className="text-border">|</span>
                                        <span>
                                            {row.getValue('owner')}
                                        </span>
                                    </div>
                                </div>
                                <DropdownMenu>
                                    <DropdownMenuTrigger
                                        className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted outline-none"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <MoreVertical className="size-4" />
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                            onSelect={() =>
                                                navigate(
                                                    `/activations/${row.getValue(
                                                        'id',
                                                    )}`,
                                                )
                                            }
                                        >
                                            Manage
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onSelect={() =>
                                                onClone(
                                                    row.getValue('id'),
                                                    row.original,
                                                )
                                            }
                                        >
                                            Clone
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            className="text-destructive"
                                            onSelect={() =>
                                                performDelete(
                                                    row.getValue('id'),
                                                    row.getValue('name'),
                                                )
                                            }
                                        >
                                            Archive
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {enablePagination && (
                <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                        Page {table.getState().pagination.pageIndex + 1} of{' '}
                        {table.getPageCount()}
                    </p>
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 mr-4">
                            <span className="text-sm text-muted-foreground">
                                Go to
                            </span>
                            <Input
                                type="number"
                                min={1}
                                max={table.getPageCount()}
                                defaultValue={
                                    table.getState().pagination.pageIndex + 1
                                }
                                onChange={(e) => {
                                    const page = e.target.value
                                        ? Number(e.target.value) - 1
                                        : 0;
                                    table.setPageIndex(page);
                                }}
                                className="h-8 w-16"
                            />
                        </div>
                        <Select
                            value={String(table.getState().pagination.pageSize)}
                            onValueChange={(value) => {
                                table.setPageSize(Number(value));
                            }}
                        >
                            <SelectTrigger className="h-8 w-[90px] text-sm">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {[25, 50, 100].map((pageSize) => (
                                    <SelectItem
                                        key={pageSize}
                                        value={String(pageSize)}
                                    >
                                        {pageSize} rows
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="flex items-center gap-1">
                            <Button
                                variant="outline"
                                size="icon"
                                className="size-8"
                                onClick={() => table.setPageIndex(0)}
                                disabled={!table.getCanPreviousPage()}
                            >
                                <ChevronsLeft className="size-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                className="size-8"
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                            >
                                <ChevronLeft className="size-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                className="size-8"
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                            >
                                <ChevronRight className="size-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                className="size-8"
                                onClick={() =>
                                    table.setPageIndex(
                                        table.getPageCount() - 1,
                                    )
                                }
                                disabled={!table.getCanNextPage()}
                            >
                                <ChevronsRight className="size-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export { createColumnHelper };
